import services from "@/config/services";

/**
 * Il giorno di una data nel formato AAAA-MM-GG che legge il backend, o null.
 *
 * Si compone dalle parti locali: toISOString() converte in UTC e con un
 * calendario che dà la mezzanotte locale sposterebbe il giorno. Una stringa
 * si passa com'è (la validazione è del server).
 */
const giornoLocale = (valore) => {
  if (typeof valore === "string") return valore.trim() || null;
  if (!(valore instanceof Date) || Number.isNaN(valore.getTime())) return null;

  const anno = String(valore.getFullYear()).padStart(4, "0");
  const mese = String(valore.getMonth() + 1).padStart(2, "0");
  const giorno = String(valore.getDate()).padStart(2, "0");
  return `${anno}-${mese}-${giorno}`;
};

/**
 * Service per la gestione dei RID SDD delle banche MPS e BPM.
 *
 * Il metodo `scarica` merita una nota: il download è deliberatamente in due
 * chiamate. La POST prenota il file e lo sposta da Inbox a Working; solo se
 * quella riesce si scaricano i byte. Invertire l'ordine, o fonderle in una
 * sola chiamata, riaprirebbe il buco per cui il generatore può duplicare un
 * file già preso in carico. L'invariante vive qui, in un punto solo.
 */
export default class RidSddBancheService {
  constructor(http, eventBus) {
    this.http = http;
    this.eventBus = eventBus;
    this.baseUrl = `${services.ask_pratiche_api.url}/management/rid-sdd-banche`;
  }

  /**
   * Elenco paginato dei file.
   *
   * @param {Object} filters - banca, stato, sequence_type, dataFrom, dataTo, search, solo_da_attribuire
   * @param {number} page
   * @param {number} perPage
   * @returns {Promise<{items: Array, total: number, page: number, lastPage: number, totaleImporto: number, isAdmin: boolean, ultimoScan: string|null}>}
   */
  async getFiles(filters = {}, page = 1, perPage = 25) {
    const url = `${this.baseUrl}/files`;

    try {
      const res = await this.http.post(url, {
        filters,
        page,
        per_page: perPage,
      });
      const data = res.data;

      return {
        items: data.data || [],
        total: data.meta?.total || 0,
        page,
        lastPage: data.meta?.last_page || 1,
        totaleImporto: data.meta?.totale_importo || 0,
        isAdmin: !!data.meta?.is_admin,
        ultimoScan: data.meta?.ultimo_scan || null,
      };
    } catch (err) {
      console.error("Errore nel caricamento dei file RID banche:", err);
      this.eventBus.emit("data-load-error", { message: err, url });
      return {
        items: [],
        total: 0,
        page,
        lastPage: 1,
        totaleImporto: 0,
        isAdmin: false,
        ultimoScan: null,
      };
    } finally {
      this.eventBus.emit("data-loaded");
    }
  }

  /** Rilegge le quattro cartelle FTP e riconcilia lo stato. */
  async scan() {
    const url = `${this.baseUrl}/scan`;
    const res = await this.http.post(url, {});
    this.eventBus.emit("data-loaded");
    return res.data;
  }

  /**
   * Prende in carico il file (Inbox → Working) e, solo se la presa in carico
   * riesce, ne scarica il contenuto.
   *
   * Se la prenotazione fallisce l'eccezione risale al chiamante e nessun byte
   * viene richiesto: il file resta in Inbox.
   */
  async scarica(id, filename) {
    const res = await this.http.post(`${this.baseUrl}/files/${id}/scarica`, {});

    // Il file è ora in Working: i byte si possono chiedere quante volte serve.
    await this.scaricaContenuto(id, filename);

    return res.data;
  }

  /**
   * Scarica il contenuto di un file già preso in carico, senza toccarne lo
   * stato. Serve per riprovare un download interrotto.
   */
  async scaricaContenuto(id, filename) {
    await this.scaricaComeFile(`${this.baseUrl}/files/${id}/contenuto`, filename || `rid_${id}.xml`);
  }

  /** Riporta il file in Inbox annullando la presa in carico. */
  async annulla(id) {
    const res = await this.http.post(`${this.baseUrl}/files/${id}/annulla`, {});
    return res.data;
  }

  /** Segna il file come firmato e inviato alla banca (Working → Sent). */
  async firmato(id) {
    const res = await this.http.post(`${this.baseUrl}/files/${id}/firmato`, {});
    return res.data;
  }

  /** Segna il file come rifiutato (Working → Trash). */
  async rifiutato(id) {
    const res = await this.http.post(`${this.baseUrl}/files/${id}/rifiutato`, {});
    return res.data;
  }

  /**
   * Riconcilia un flusso firmato con la contabilità: per ogni disposizione
   * dice se pagamento e mandato risultano davvero registrati.
   * È una lettura, non modifica nulla.
   */
  async verificaPagamenti(id) {
    const url = `${this.baseUrl}/files/${id}/verifica-pagamenti`;
    const res = await this.http.get(url);
    this.eventBus.emit("data-loaded");
    return res.data;
  }

  /** Esegue una delle azioni di transizione per nome. */
  async azione(nome, id) {
    switch (nome) {
      case "annulla":
        return this.annulla(id);
      case "firmato":
        return this.firmato(id);
      case "rifiutato":
        return this.rifiutato(id);
      default:
        throw new Error(`Azione non riconosciuta: ${nome}`);
    }
  }

  /** Storico completo dei movimenti di un file. */
  async storico(id) {
    const url = `${this.baseUrl}/files/${id}/storico`;

    try {
      const res = await this.http.get(url);
      return res.data?.data || [];
    } catch (err) {
      console.error("Errore nel caricamento dello storico:", err);
      this.eventBus.emit("data-load-error", { message: err, url });
      return [];
    } finally {
      this.eventBus.emit("data-loaded");
    }
  }

  /** Diagnostica dello schema delle tabelle. */
  async schemaCheck() {
    const res = await this.http.get(`${this.baseUrl}/schema/check`);
    return res.data;
  }

  // ── Import degli insoluti (PDF della banca) ──────────────────────────
  //
  // Caricamento, conferma e riepilogo rispondono tutti con la stessa forma:
  // { import, gia_importato_il, conteggi, righe }. Gli errori risalgono al
  // chiamante con il messaggio del server in err.response.data.message.

  /**
   * Carica il PDF degli insoluti e ne restituisce l'anteprima. Nessuno storno
   * viene toccato finché l'import non è confermato.
   *
   * @param {File} file
   */
  async caricaInsoluti(file) {
    const fd = new FormData();
    fd.append("file", file);

    try {
      const res = await this.http.post(`${this.baseUrl}/insoluti`, fd);
      return res.data;
    } finally {
      this.eventBus.emit("data-loaded");
    }
  }

  /**
   * Storico paginato degli import, dal più recente.
   *
   * @returns {Promise<{items: Array, total: number, page: number, lastPage: number}>}
   */
  async getStoricoInsoluti(page = 1, perPage = 10) {
    const url = `${this.baseUrl}/insoluti`;

    try {
      const res = await this.http.get(url, { params: { page, per_page: perPage } });
      const data = res.data;

      return {
        items: data.data || [],
        total: data.meta?.total || 0,
        page: data.meta?.page || page,
        lastPage: data.meta?.last_page || 1,
      };
    } finally {
      this.eventBus.emit("data-loaded");
    }
  }

  /**
   * Righe ignorate di tutti gli import, cioè quelle che l'import non ha
   * applicato (ogni esito tranne aggiornabile e aggiornato): dall'import più
   * recente e, dentro l'import, per riga del PDF. Ogni riga porta i dati del
   * suo import in `import`; i candidati sono solo id di storno.
   *
   * I filtri si combinano in AND e viaggiano solo se valorizzati. `esiti` è
   * un array: Axios lo scrive `esiti[]=a&esiti[]=b`, la forma che il backend
   * legge (senza parentesi PHP tiene solo l'ultimo valore, senza errore).
   * `dal` e `al` valgono sulla scadenza della riga e escludono le righe che
   * non ne hanno una.
   *
   * Il totale e le pagine seguono tutti i filtri. I conteggi per esito
   * seguono tutti i filtri tranne gli esiti, e non cambiano con la pagina:
   * con degli esiti scelti mostrano comunque la distribuzione dentro import,
   * date e testo.
   *
   * @param {Object} filtri
   * @param {string[]} [filtri.esiti] - esiti ignorati; vuoto per tutti
   * @param {number|string|null} [filtri.importId] - solo le righe di quell'import
   * @param {Date|string|null} [filtri.dal] - scadenza minima, inclusa (Date o AAAA-MM-GG)
   * @param {Date|string|null} [filtri.al] - scadenza massima, inclusa (Date o AAAA-MM-GG)
   * @param {string} [filtri.cerca] - testo nel nome del debitore o nell'IBAN
   * @returns {Promise<{items: Array, total: number, page: number, lastPage: number, conteggi: Object}>}
   */
  async getIgnoratiInsoluti(filtri = {}, page = 1, perPage = 10) {
    const url = `${this.baseUrl}/insoluti/ignorati`;
    const params = { page, per_page: perPage };

    const esiti = Array.isArray(filtri.esiti) ? filtri.esiti.filter(Boolean) : [];
    if (esiti.length) params.esiti = esiti;

    if (filtri.importId !== null && filtri.importId !== undefined && filtri.importId !== "") {
      params.import_id = filtri.importId;
    }

    const dal = giornoLocale(filtri.dal);
    if (dal) params.dal = dal;

    const al = giornoLocale(filtri.al);
    if (al) params.al = al;

    const cerca = typeof filtri.cerca === "string" ? filtri.cerca.trim() : "";
    if (cerca) params.cerca = cerca;

    try {
      const res = await this.http.get(url, { params });
      const data = res.data;

      return {
        items: data.data || [],
        total: data.meta?.total || 0,
        page: data.meta?.page || page,
        lastPage: data.meta?.last_page || 1,
        conteggi: data.conteggi || {},
      };
    } finally {
      this.eventBus.emit("data-loaded");
    }
  }

  /** Riepilogo di un import: dati, conteggi per esito e righe. */
  async getImportInsoluti(id) {
    try {
      const res = await this.http.get(`${this.baseUrl}/insoluti/${id}`);
      return res.data;
    } finally {
      this.eventBus.emit("data-loaded");
    }
  }

  /**
   * Conferma l'import: scrive la motivazione negli storni ancora liberi.
   * Dopo un errore lo stato dell'import non si deduce dalla risposta
   * (alcune righe possono essere già state scritte): va riletto con
   * getImportInsoluti.
   */
  async confermaInsoluti(id) {
    try {
      const res = await this.http.post(`${this.baseUrl}/insoluti/${id}/conferma`, {});
      return res.data;
    } finally {
      this.eventBus.emit("data-loaded");
    }
  }

  /** Scarica il PDF archiviato con il nome caricato dall'operatore. */
  async scaricaPdfInsoluti(id, filename) {
    try {
      await this.scaricaComeFile(
        `${this.baseUrl}/insoluti/${id}/pdf`,
        filename || `insoluti_${id}.pdf`,
        "application/pdf"
      );
    } finally {
      this.eventBus.emit("data-loaded");
    }
  }

  /**
   * Scarica i byte di `url` e li fa salvare al browser con il nome indicato.
   *
   * Con responseType blob anche il corpo di un errore arriva come Blob: lo si
   * rilegge come JSON, così il chiamante trova il messaggio del server in
   * err.response.data.message (per esempio il 409 di un file non più sull'FTP).
   */
  async scaricaComeFile(url, nome, tipo = "") {
    let response;

    try {
      response = await this.http({ url, method: "GET", responseType: "blob" });
    } catch (err) {
      if (err?.response?.data instanceof Blob) {
        try {
          err.response.data = JSON.parse(await err.response.data.text());
        } catch (parseError) {
          // Corpo non JSON: resta l'errore originale.
        }
      }
      throw err;
    }

    const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: tipo }));
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", nome);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  }
}
